/**
 * DynamicSidebar Component - Sidebar with dynamic menu support
 * Created: 1405/02/17
 */

import React, { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Tooltip,
  Divider,
  Button,
  Typography,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import { menuIconRegistry } from './iconRegistry';
import { MergedMenuItem, MenuTarget } from '../menu/types';
import { useMenu } from '../menu/hooks/useMenu';
import { useMenuPermissions } from '../menu/hooks/useMenuPermissions';
import { useAuth } from '../contexts/AuthContext';
import { getAllForms } from '../forms/registry';

interface DynamicSidebarProps {
  open: boolean;
  drawerWidth: number;
  collapsedWidth: number;
  anchor?: 'left' | 'right';
  isMobile?: boolean;
  onMobileClose?: () => void;
  onOpen?: () => void;
  onClose?: () => void;
}

// Icon mapping from string to component — از رجیستری صریح (به‌جای import * که
// همه ۲۰۰۰+ آیکون MUI را داخل main bundle می‌کشید)
const getIconComponent = (iconName?: string): React.ReactNode => {
  if (!iconName) return null;

  const IconComponent = menuIconRegistry[iconName];
  if (IconComponent) {
    return <IconComponent />;
  }

  return null;
};

// Get route from menu target - resolves formId to actual route via form registry
const getRouteFromTarget = (target: MenuTarget): string => {
  switch (target.type) {
    case 'internal': {
      if (target.route && target.route !== '#') return target.route;
      // اگر reportCode وجود دارد، مستقیماً به صفحه گزارش برو
      if (target.reportCode) return `/reports/view/${target.reportCode}`;
      if (target.formId) {
        const form = getAllForms().find(f => f.id === target.formId);
        if (form?.defaultRoute) return form.defaultRoute;
      }
      return '#';
    }
    case 'external':
      return target.url || '#';
    case 'report':
      return `/reports/view/${target.reportCode}`;
    case 'divider':
      return '#';
    default:
      return '#';
  }
};

const DynamicSidebar: React.FC<DynamicSidebarProps> = ({
  open,
  drawerWidth,
  collapsedWidth,
  anchor = 'right',
  isMobile = false,
  onMobileClose,
  onOpen,
  onClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const { menuTree, isLoading: isMenuLoading, isError: isMenuError, refreshMenu } = useMenu();
  const { filteredTree } = useMenuPermissions(
    [],
    menuTree,
    user ? { id: String(user.id), role: String(user.role), permissions: [] } : null
  );
  
  // Track expanded state of parent menus
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());
  
  const handleToggleExpand = useCallback((item: MergedMenuItem, siblings: MergedMenuItem[]) => {
    setExpandedMenus(prev => {
      const newSet = new Set(prev);
      const isCurrentlyOpen = newSet.has(item.id);
      siblings.forEach(s => newSet.delete(s.id));
      if (!isCurrentlyOpen) {
        newSet.add(item.id);
      }
      return newSet;
    });
  }, []);
  
  const handleMenuClick = useCallback((item: MergedMenuItem, siblings: MergedMenuItem[], e: React.MouseEvent) => {
    if (item.children && item.children.length > 0) {
      e.preventDefault();
      handleToggleExpand(item, siblings);
      if (!open && onOpen) {
        onOpen();
      }
    } else {
      const route = getRouteFromTarget(item.target);
      if (route !== '#') {
        if (item.target.type === 'external' && item.target.openInNewTab) {
          return;
        }
        // middle-click یا ctrl+click → مرورگر خودش تب جدید باز می‌کند
        if (e.button === 1 || e.ctrlKey || e.metaKey) {
          return;
        }
        e.preventDefault();
        navigate(route);
        if (onClose) {
          onClose();
        }
      }
    }
  }, [navigate, handleToggleExpand, open, onOpen, onClose]);
  
  // Check if menu item is active
  const isMenuActive = useCallback((item: MergedMenuItem): boolean => {
    const route = getRouteFromTarget(item.target);
    if (route === '#') return false;
    
    return location.pathname === route || 
           (item.children?.some(child => isMenuActive(child)) ?? false);
  }, [location.pathname]);
  
  // Render menu item recursively
  const renderMenuItem = (item: MergedMenuItem, siblings: MergedMenuItem[], level: number = 0) => {
    // Skip divider items
    if (item.target.type === 'divider') {
      return (
        <Divider 
          key={item.id} 
          sx={{ 
            my: 1, 
            opacity: open ? 1 : 0,
            transition: 'opacity 0.3s'
          }} 
        />
      );
    }
    
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus.has(item.id);
    const isActive = isMenuActive(item);
    
    const icon = getIconComponent(item.icon);
    
    const route = getRouteFromTarget(item.target);
    const isLink = !hasChildren && route !== '#';

    const levelBgColors = [
      'transparent',
      'rgba(99, 102, 241, 0.07)',
      'rgba(99, 102, 241, 0.13)',
      'rgba(99, 102, 241, 0.19)',
      'rgba(99, 102, 241, 0.25)',
      'rgba(99, 102, 241, 0.31)',
    ];
    const levelBg = levelBgColors[Math.min(level, levelBgColors.length - 1)];

    return (
      <React.Fragment key={item.id}>
        <Tooltip 
          title={!open ? item.title : ''} 
          placement="right"
          arrow
        >
          <ListItem disablePadding>
            <ListItemButton
              selected={isActive}
              component={isLink ? 'a' : 'div'}
              href={isLink ? route : undefined}
              onClick={(e: React.MouseEvent) => handleMenuClick(item, siblings, e)}
              sx={{
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
                px: open ? 2.5 : 1.5,
                pl: open ? 2.5 + level * 2 : 1.5,
                textDecoration: 'none',
                color: 'inherit',
                backgroundColor: levelBg,
                '&:hover': {
                  backgroundColor: `rgba(99, 102, 241, ${0.12 + level * 0.06})`,
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 2 : 'auto',
                  justifyContent: 'center',
                }}
              >
                {icon}
              </ListItemIcon>
              <ListItemText
                primary={item.title}
                sx={{
                  opacity: open ? 1 : 0,
                  transition: 'opacity 0.3s',
                  '& .MuiTypography-root': {
                    fontWeight: isActive ? 600 : 400,
                  },
                }}
              />
              {hasChildren && open && (
                isExpanded ? <ExpandLess /> : <ExpandMore />
              )}
            </ListItemButton>
          </ListItem>
        </Tooltip>
        
        {hasChildren && (
          <Collapse in={isExpanded && open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map(child => renderMenuItem(child, item.children!, level + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };
  
  const drawerContent = (
    <Box sx={{ overflow: 'auto', height: 'calc(100vh - 64px)', pt: 8, '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
      {isMenuLoading ? (
        <List>
          {[1,2,3,4,5].map(i => (
            <ListItem key={i} disablePadding>
              <ListItemButton sx={{ minHeight: 48, px: 2.5 }}>
                <ListItemIcon sx={{ minWidth: 0, mr: 2 }}>
                  <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'action.hover' }} />
                </ListItemIcon>
                {open && <Box sx={{ width: 120, height: 16, borderRadius: 1, bgcolor: 'action.hover' }} />}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      ) : isMenuError ? (
        <Box sx={{ p: 3, textAlign: 'center', direction: 'rtl' }}>
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            خطا در بارگذاری منو
          </Typography>
          <Button variant="outlined" size="small" onClick={refreshMenu}>
            تلاش مجدد
          </Button>
        </Box>
      ) : (
        <List>
          {filteredTree.map(item => renderMenuItem(item, filteredTree))}
        </List>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        anchor={anchor}
        open={open}
        onClose={onMobileClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            boxShadow: 6,
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      anchor={anchor}
      open={open}
      sx={{
        width: open ? drawerWidth : collapsedWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        transition: 'width 0.3s',
        '& .MuiDrawer-paper': {
          width: open ? drawerWidth : collapsedWidth,
          transition: 'width 0.3s',
          overflowX: 'hidden',
          boxSizing: 'border-box',
          boxShadow: 3,
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default DynamicSidebar;
